/**
 * UserStatusModal Component
 * Confirmation dialog for activating or deactivating a team user.
 */

import { useState } from 'react'
import { AlertTriangle, CheckCircle2, UserX, UserCheck, X } from 'lucide-react'
import type { User } from '@/types/user.types'
import { getRoleDisplayName } from '@/types/auth.types'

interface UserStatusModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  user: User | null
  action: 'activate' | 'deactivate'
}

export function UserStatusModal({
  isOpen,
  onClose,
  onConfirm,
  user,
  action,
}: UserStatusModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!isOpen || !user) return null

  const isDeactivate = action === 'deactivate'

  const handleConfirm = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      await onConfirm()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} user.`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-status-modal-title"
    >
      <div className="relative w-full max-w-md bg-surface-light dark:bg-surface-dark rounded-2xl shadow-2xl border border-surface-overlay overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-surface-overlay">
          <div className="flex items-center gap-3">
            <div
              className={`p-2.5 rounded-xl ${
                isDeactivate
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
              }`}
            >
              {isDeactivate ? (
                <UserX className="w-5 h-5" />
              ) : (
                <UserCheck className="w-5 h-5" />
              )}
            </div>
            <div>
              <h2
                id="user-status-modal-title"
                className="text-base font-semibold text-text-primary"
              >
                {isDeactivate ? 'Deactivate User Account' : 'Activate User Account'}
              </h2>
              <p className="text-xs text-text-tertiary">
                {isDeactivate ? 'Suspend login access' : 'Restore login access'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1.5 rounded-lg text-text-tertiary hover:text-text-primary hover:bg-surface-overlay transition-colors disabled:opacity-50"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* User Target Card */}
          <div className="p-3.5 rounded-xl bg-surface-overlay/50 border border-surface-overlay flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center font-bold text-sm">
              {user.fullName
                .split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-text-primary truncate">
                {user.fullName}
              </p>
              <p className="text-xs text-text-tertiary truncate">{user.email}</p>
            </div>
            <span className="badge badge-sm badge-outline">
              {getRoleDisplayName(user.role)}
            </span>
          </div>

          {/* Explanation */}
          <div className="space-y-2 text-xs text-text-secondary leading-relaxed">
            {isDeactivate ? (
              <>
                <p>
                  Are you sure you want to deactivate <strong>{user.fullName}</strong>?
                </p>
                <ul className="space-y-1.5 pl-4 list-disc text-text-tertiary">
                  <li>
                    The user will immediately be <strong>signed out of all active sessions</strong>.
                  </li>
                  <li>
                    They will be <strong>blocked from signing in</strong> until reactivated.
                  </li>
                  <li>
                    All historical client records, documents, and audit logs will remain intact.
                  </li>
                </ul>
              </>
            ) : (
              <>
                <p>
                  Are you sure you want to reactivate <strong>{user.fullName}</strong>?
                </p>
                <ul className="space-y-1.5 pl-4 list-disc text-text-tertiary">
                  <li>
                    The user will be permitted to <strong>log in to TaxHive again</strong>.
                  </li>
                  <li>
                    Their role (<strong>{getRoleDisplayName(user.role)}</strong>) and assigned privileges will be active immediately.
                  </li>
                </ul>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 bg-surface-overlay/20 border-t border-surface-overlay">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="btn btn-ghost btn-sm text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className={`btn btn-sm text-xs gap-1.5 ${
              isDeactivate ? 'btn-error' : 'btn-primary'
            }`}
          >
            {isSubmitting ? (
              <span className="loading loading-spinner loading-xs" />
            ) : isDeactivate ? (
              <UserX className="w-3.5 h-3.5" />
            ) : (
              <CheckCircle2 className="w-3.5 h-3.5" />
            )}
            {isDeactivate ? 'Deactivate User' : 'Activate User'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default UserStatusModal
